import {
  BitkitAlert,
  BitkitButton,
  BitkitCloseButton,
  BitkitIconButton,
  IconArrowUp,
  IconSparkleFilled,
  IconStop,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { Textarea } from '@chakra-ui/react/textarea';
import { KeyboardEventHandler, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

type AIDrawerProps = {
  children: ReactNode;
  isInputDisabled?: boolean;
  isLoading?: boolean;
  isStopping?: boolean;
  errorMessage?: string;
  message: string;
  moreActions?: ReactNode;
  onErrorDismiss?: () => void;
  onHide: () => void;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  onStop?: () => void;
  title: string;
  titleBadge?: ReactNode;
  top: number;
};

const AIDrawer = (props: AIDrawerProps) => {
  const {
    children,
    errorMessage,
    isInputDisabled,
    isLoading,
    isStopping,
    message,
    moreActions,
    onErrorDismiss,
    onHide,
    onMessageChange,
    onSend,
    onStop,
    title,
    titleBadge,
    top,
  } = props;

  const [atBottom, setAtBottom] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollToBottom = () => {
      if (atBottom) {
        el.scrollTo(0, el.scrollHeight);
      }
    };

    const observer = new MutationObserver(scrollToBottom);
    observer.observe(el, { childList: true, subtree: true, characterData: true });

    const resizeObserver = new ResizeObserver(scrollToBottom);
    for (const child of el.children) {
      resizeObserver.observe(child);
    }

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [atBottom]);

  const handleScroll = useCallback(
    (ev: { currentTarget: HTMLElement }) => {
      if (!setAtBottom) return;
      const el = ev.currentTarget;
      const atBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) <= 1;
      setAtBottom(atBottom);
    },
    [setAtBottom],
  );

  const handleKeyDown: KeyboardEventHandler = (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();

      if (isInputDisabled) {
        return;
      }

      if (ev.shiftKey) {
        onMessageChange(`${message}\n`);
      } else if (message.trim()) {
        onSend();
      }
    }
  };

  return (
    <Box
      pointerEvents="all"
      boxShadow="elevation/md"
      background="gradient/ai/background/surface"
      border="1px solid"
      borderColor="border/regular"
      borderRadius="12"
      overflow="hidden"
      width="min(25rem, 100%)"
      position="fixed"
      top={`calc(${top}px + 16px)`}
      bottom="16"
      right="16"
      display="flex"
      flexDirection="column"
    >
      <Box
        as="header"
        padding="24"
        paddingBottom="16"
        display="flex"
        borderBottom="1px solid"
        borderBottomColor="border/regular"
        alignItems="center"
        gap="8"
      >
        <IconSparkleFilled color="color/indigo/base" />
        <Box display="flex" alignItems="center" gap="12" flexGrow="1">
          <Text as="h3" textStyle="heading/h3">
            {title}
          </Text>
          {titleBadge}
        </Box>
        {moreActions}
        <BitkitCloseButton colorVariant="neutral" onClick={onHide} ml="4" />
      </Box>
      <Box
        ref={scrollRef}
        overscrollBehavior="none"
        padding="16"
        flexGrow="1"
        overflowY="auto"
        onScroll={handleScroll}
        display="flex"
        flexDirection="column"
        gap="16"
      >
        {children}
      </Box>
      <Box
        as="footer"
        padding="16"
        borderTop="1px solid"
        borderTopColor="border/regular"
        display="flex"
        flexDirection="column"
        gap="8"
      >
        {errorMessage && (
          <BitkitAlert variant="critical" messageText={errorMessage} dismissible onClose={onErrorDismiss} />
        )}
        <Box
          borderRadius="8"
          border="1px solid"
          borderColor="border/regular"
          background={isInputDisabled ? 'background/disabled' : 'background/primary'}
          boxShadow="inset/field"
          paddingX="12"
          paddingBottom="12"
          paddingTop="8"
          _focusWithin={{
            outline: '3px solid',
            outlineColor: 'color/purple/highlight',
            outlineOffset: '0',
          }}
          textAlign="end"
        >
          <Textarea
            value={message}
            disabled={isInputDisabled}
            onChange={(ev) => onMessageChange(ev.currentTarget.value)}
            onKeyDown={handleKeyDown}
            textStyle="body/md/regular"
            placeholder="Ask a question, or request a change."
            _placeholder={{ color: isInputDisabled ? 'text/disabled' : undefined }}
            style={{ outline: 'none', boxShadow: 'none' }}
            resize="none"
            width="100%"
            height="52px"
            border="none"
            background="transparent"
            padding="0"
            unstyled
          />
          <Box display="flex" justifyContent="flex-end">
            {onStop ? (
              <BitkitButton
                state={isStopping ? 'loading' : undefined}
                onClick={onStop}
                size="sm"
                variant="secondary"
                icon={IconStop}
              >
                Stop
              </BitkitButton>
            ) : (
              <BitkitIconButton
                label="Send"
                size="sm"
                icon={IconArrowUp}
                state={isLoading ? 'loading' : !message.trim() ? 'disabled' : undefined}
                onClick={onSend}
                variant="ai-primary"
                type="submit"
              />
            )}
          </Box>
        </Box>
        <Text textStyle="body/sm/regular" color="text/tertiary" textAlign="center">
          Assistant can be inaccurate. Review changes before saving.
        </Text>
      </Box>
    </Box>
  );
};

export default AIDrawer;
